import { useMainLayoutPage } from "app/layouts/MainLayout/context";
import { useAuthUserStore } from "entities/auth/stores/authUserStore";
import { createSupportMessage } from "entities/customerTickets/api/createSupportMessage";
import { useReadAllMessages } from "entities/customerTickets/mutations/useReadAllMessages";
import { useReorderTicketMessages } from "entities/customerTickets/mutations/useReorderTicketMessages";
import { useUpdateTicketStatus } from "entities/customerTickets/mutations/useUpdateTicketStatus";
import {
  CustomerSupportTicket,
  TicketStatus,
} from "entities/customerTickets/types";
import { useTicketMessagesList } from "modules/customerTickets/hooks/useTicketMessagesList";
import React, { FC, useState, PropsWithChildren, useEffect } from "react";
import useInputState, { InputHookResult } from "shared/hooks/useInputState";
import { createContext } from "shared/utils/context";
import { useCustomerTicketsData } from "../../../hooks/useCustomerTicketsData";
import { useTicketsFilter } from "../../../hooks/useTicketsFilter";
import { formattedFilters } from "../../../hooks/utils";
import { getRandomColor } from "../helpers";
import { CustomerTicketsPageType, TicketAvatar } from "./types";
import { doc, onSnapshot, getFirestore } from "firebase/firestore";

type ColorMap = {
  [id: string]: string;
};

const ROWS_PER_PAGE = 100;

const [Provider, useCustomerTicketsPage] =
  createContext<CustomerTicketsPageType>({
    name: "CustomerTicketsPageContext",
    hookName: "useCustomerTicketsPage",
    providerName: "CustomerTicketsPageProvider",
  });

const CustomerTicketsPageProvider: FC<PropsWithChildren> = ({ children }) => {
  const { decrementUnreadMessagesCount } = useMainLayoutPage();
  const db = getFirestore();
  const messageText: InputHookResult = useInputState("messageText", "");
  const [ticketsBgColors, setTicketsBgColors] = useState<TicketAvatar[]>([]);
  const [ticketStatus, setTicketStatus] = useState<TicketStatus | null>(null);
  const [selectedTicket, setSelectedTicket] =
    useState<CustomerSupportTicket>();
  const [selectedTicketId, setSelectedTicketId] =
    useState<string>();
  const [isSubmittingMessage, setIsSubmittingMessage] =
    useState<boolean>(false);
  const { firebaseUser } = useAuthUserStore();
  const userEmail = firebaseUser ? firebaseUser?.email || "" : undefined;

  const { filtersData, filteredValues } = useTicketsFilter();
  const {
    searchData,
    isLoading,
    tableData,
    moveTicketToTop,
    updateCustomerTicketStatus,
    updateTicketAdminUnreadMessagesCount,
  } = useCustomerTicketsData(filteredValues);

  const filters = formattedFilters(filteredValues);

  const { mutate: updateTicketStatusMutate } = useUpdateTicketStatus();

  const { mutate: readAllMessagesMutate } = useReadAllMessages();
  const { mutate: reorderTicketMessagesMutate } = useReorderTicketMessages();

  const {
    data,
    totalMessages,
    loading: isLoadingMessages,
    fetchNextPage,
  } = useTicketMessagesList({
    initialPaginationParams: {
      limit: ROWS_PER_PAGE,
      endListItem: null,
    },
    ticketId: selectedTicket?.id ?? "",
    readAllTicketMessages: (ticketId: string) =>
      readAllMessagesMutate({
        ticketId,
        query: searchData.value,
        filters,
        onMutate: () => updateTicketAdminUnreadMessagesCount(ticketId),
      }),
    reorderTickets: (ticketId: string) => {
      reorderTicketMessagesMutate({
        ticketId,
        query: searchData.value,
        filters,
        onMutate: () => moveTicketToTop(ticketId),
      });
    },
  });

  useEffect(() => {
    if (tableData.data.length > 0) {
      const currentColors = [...ticketsBgColors];

      const colorMap: { [key: string]: string } =
        currentColors.reduce<ColorMap>((acc, { id, bgColor }) => {
          acc[id] = bgColor;
          return acc;
        }, {});

      const newColors = tableData.data.map((ticket) => {
        const existingColor = colorMap[ticket.id];
        const bgColor = existingColor || getRandomColor();

        return {
          id: ticket.id,
          bgColor,
        };
      });

      setTicketsBgColors(newColors);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableData.data]);

  useEffect(() => {
    if (selectedTicket) {
      setTicketStatus(selectedTicket.status);
    }
  }, [selectedTicket]);

  useEffect(() => {
    if (selectedTicketId) {
      const unsubscribe = onSnapshot(
        doc(db, "customerSupportTickets", selectedTicketId),
        (doc) => {
          const ticket = {
            id: doc.id,
            ...doc.data(),
            createdAt: doc.data()?.createdAt.toDate(),
            updatedAt: doc.data()?.updatedAt.toDate(),
            adminUnreadMessagesCount: 0,
          } as CustomerSupportTicket;
          setSelectedTicket(ticket);
        });

      return () => {
        unsubscribe();
      };
    }
  }, [db, selectedTicketId]);

  const handleSelectMessage = async (ticket: CustomerSupportTicket) => {
    setSelectedTicket({ ...ticket, adminUnreadMessagesCount: 0 });
    setSelectedTicketId(ticket.id);
    decrementUnreadMessagesCount(ticket.adminUnreadMessagesCount);
  };

  const handlePaginationChange = async () => {
    await fetchNextPage();
  };

  const onCreateSupportMessage = async () => {
    try {
      if (!selectedTicket?.id) return;
      setIsSubmittingMessage(true);

      const newMessage = {
        ticketId: selectedTicket.id,
        message: messageText.input.value,
        adminEmail: userEmail ?? "",
      };

      await createSupportMessage(newMessage);

      messageText.setValue("");
    } catch (e) {
      console.error(e);
    } finally {
      setIsSubmittingMessage(false);
    }
  };

  const handleChangeTicketStatus = async (status: string) => {
    if (selectedTicket) {
      setTicketStatus(status as TicketStatus);
      await updateTicketStatusMutate({
        ticketId: selectedTicket.id,
        status: status as TicketStatus,
        onMutate: () =>
          updateCustomerTicketStatus(selectedTicket.id, status as TicketStatus),
        filters,
        query: searchData.value,
      });
    }
  };

  // eslint-disable-next-line react/jsx-no-constructed-context-values
  const value = {
    loading: isLoading,
    isLoadingTicketMessages: isLoadingMessages,
    selectedUserId: selectedTicket?.userId ?? "",
    onCreateSupportMessage,
    tableData,
    selectedTicket,
    ticketMessages: data,
    ticketsBgColors,
    messageText,
    ticketStatus,
    totalTicketMessagesCount: totalMessages,
    handleChangeTicketStatus,
    handlePaginationChange,
    handleSelectMessage,
    searchData,
    filtersData,
    isSubmittingSupportMessage: isSubmittingMessage,
  };

  return <Provider value={value}>{children}</Provider>;
};

export { CustomerTicketsPageProvider, useCustomerTicketsPage };
